import type { InputSystem, Action } from "../input/Input";
import { ACTION_LABELS, keyLabel } from "../input/Input";

const ORDER: Action[] = [
  "pitchUp", "pitchDown", "yawLeft", "yawRight", "rollLeft", "rollRight",
  "throttleUp", "throttleDown", "fire", "skill1", "skill2", "skill3",
  "boostToRing",
  "openChat", "toggleKeymap", "openSettings", "respawn",
];

export class Settings {
  private el = document.getElementById("settings")!;
  private bindList = document.getElementById("bindList")!;
  private invertPitch = document.getElementById("invertPitch") as HTMLInputElement;
  private mouseAimChk = document.getElementById("mouseAimChk") as HTMLInputElement;
  private autoLevelChk = document.getElementById("autoLevelChk") as HTMLInputElement;
  private mouseSensRange = document.getElementById("mouseSensRange") as HTMLInputElement;
  private mouseSensReadout = document.getElementById("mouseSensReadout")!;
  private masterVol = document.getElementById("masterVol") as HTMLInputElement;
  private sfxVol = document.getElementById("sfxVol") as HTMLInputElement;
  private engineVol = document.getElementById("engineVol") as HTMLInputElement;
  private mutedChk = document.getElementById("mutedChk") as HTMLInputElement;
  private resetBindBtn = document.getElementById("resetBindBtn") as HTMLButtonElement;
  private closeBtn = document.getElementById("settingsClose") as HTMLButtonElement;
  private leaveBtn = document.getElementById("leaveBtn") as HTMLButtonElement;
  private masterVolReadout = document.getElementById("masterVolReadout")!;
  private sfxVolReadout = document.getElementById("sfxVolReadout")!;
  private engineVolReadout = document.getElementById("engineVolReadout")!;
  private listening: { action: Action; el: HTMLElement } | null = null;
  private onChange: () => void;

  constructor(private input: InputSystem, private onLeave: () => void, onChange: () => void) {
    this.onChange = onChange;
    this.invertPitch.checked = input.settings.invertPitch;
    this.masterVol.value = String(input.settings.masterVolume);
    this.sfxVol.value = String(input.settings.sfxVolume);
    this.engineVol.value = String(input.settings.engineVolume);
    this.mutedChk.checked = input.settings.muted;
    this.updateVolReadouts();

    // Tabs
    this.el.querySelectorAll<HTMLButtonElement>(".settings-tab").forEach(btn => {
      btn.addEventListener("click", () => this.selectTab(btn.dataset.tab as string));
    });
    // Backdrop click to close
    this.el.querySelectorAll<HTMLElement>("[data-close]").forEach(e => {
      e.addEventListener("click", () => this.hide());
    });

    this.invertPitch.addEventListener("change", () => {
      this.input.settings.invertPitch = this.invertPitch.checked;
      this.input.saveSettings();
    });
    this.mouseAimChk.addEventListener("change", () => {
      this.input.settings.mouseAim = this.mouseAimChk.checked;
      this.input.saveSettings();
      this.onChange();
    });
    this.autoLevelChk.addEventListener("change", () => {
      this.input.settings.autoLevel = this.autoLevelChk.checked;
      this.input.saveSettings();
    });
    this.mouseSensRange.addEventListener("input", () => {
      this.input.settings.mouseSens = parseFloat(this.mouseSensRange.value);
      this.input.saveSettings();
      this.mouseSensReadout.textContent = this.input.settings.mouseSens.toFixed(2) + "×";
    });
    this.masterVol.addEventListener("input", () => {
      this.input.settings.masterVolume = parseFloat(this.masterVol.value);
      this.input.saveSettings();
      this.onChange();
      this.updateVolReadouts();
    });
    this.sfxVol.addEventListener("input", () => {
      this.input.settings.sfxVolume = parseFloat(this.sfxVol.value);
      this.input.saveSettings();
      this.onChange();
      this.updateVolReadouts();
    });
    this.engineVol.addEventListener("input", () => {
      this.input.settings.engineVolume = parseFloat(this.engineVol.value);
      this.input.saveSettings();
      this.onChange();
      this.updateVolReadouts();
    });
    this.mutedChk.addEventListener("change", () => {
      this.input.settings.muted = this.mutedChk.checked;
      this.input.saveSettings();
      this.onChange();
    });
    this.resetBindBtn.addEventListener("click", () => {
      this.input.resetBindings();
      this.render();
    });
    this.closeBtn.addEventListener("click", () => this.hide());
    this.leaveBtn.addEventListener("click", () => { this.hide(); this.onLeave(); });

    window.addEventListener("keydown", this.onCapture, true);
    window.addEventListener("mousedown", this.onCaptureMouse, true);
  }

  private onCapture = (e: KeyboardEvent) => {
    if (!this.listening) return;
    if (e.code === "Tab") return;
    e.preventDefault();
    e.stopPropagation();
    this.input.rebind(this.listening.action, e.code);
    const wasListening = this.listening;
    this.listening = null;
    wasListening.el.classList.remove("listening");
    this.render();
  };
  private onCaptureMouse = (e: MouseEvent) => {
    if (!this.listening) return;
    e.preventDefault();
    e.stopPropagation();
    this.input.rebind(this.listening.action, "Mouse" + e.button);
    const wasListening = this.listening;
    this.listening = null;
    wasListening.el.classList.remove("listening");
    this.render();
  };

  toggle() { if (this.el.classList.contains("hidden")) this.show(); else this.hide(); }
  show() { this.render(); this.el.classList.remove("hidden"); this.input.setLockedToUI(true); }
  hide() { this.el.classList.add("hidden"); this.input.setLockedToUI(false); }
  isOpen() { return !this.el.classList.contains("hidden"); }

  private updateVolReadouts() {
    this.masterVolReadout.textContent = Math.round(this.input.settings.masterVolume * 100) + "%";
    this.sfxVolReadout.textContent = Math.round(this.input.settings.sfxVolume * 100) + "%";
    this.engineVolReadout.textContent = Math.round(this.input.settings.engineVolume * 100) + "%";
  }

  private selectTab(name: string) {
    this.el.querySelectorAll<HTMLButtonElement>(".settings-tab").forEach(b => {
      b.classList.toggle("active", b.dataset.tab === name);
    });
    this.el.querySelectorAll<HTMLElement>(".settings-pane").forEach(p => {
      p.classList.toggle("hidden", p.dataset.pane !== name);
    });
  }

  private render() {
    this.invertPitch.checked = this.input.settings.invertPitch;
    this.mouseAimChk.checked = this.input.settings.mouseAim;
    this.autoLevelChk.checked = this.input.settings.autoLevel;
    this.mouseSensRange.value = String(this.input.settings.mouseSens);
    this.mouseSensReadout.textContent = this.input.settings.mouseSens.toFixed(2) + "×";
    this.masterVol.value = String(this.input.settings.masterVolume);
    this.sfxVol.value = String(this.input.settings.sfxVolume);
    this.engineVol.value = String(this.input.settings.engineVolume);
    this.mutedChk.checked = this.input.settings.muted;
    this.bindList.innerHTML = "";
    for (const a of ORDER) {
      const label = document.createElement("div");
      label.className = "action";
      label.textContent = ACTION_LABELS[a];
      this.bindList.appendChild(label);
      const wrap = document.createElement("div");
      const keys = this.input.bindings[a];
      const text = keys.length ? keys.map(keyLabel).join(", ") : "—";
      const btn = document.createElement("button");
      btn.className = "keybtn";
      btn.textContent = text + "  +rebind";
      btn.addEventListener("click", () => {
        if (this.listening) this.listening.el.classList.remove("listening");
        btn.classList.add("listening");
        btn.textContent = "press a key…";
        this.listening = { action: a, el: btn };
      });
      wrap.appendChild(btn);
      this.bindList.appendChild(wrap);
    }
  }
}
